import { createClient } from "@supabase/supabase-js";

const runtimeConfig =
  typeof window === "undefined" ? {} : window.__EFABRIC_CONFIG__ || {};

const frontendSupabaseUrl = runtimeConfig.frontendSupabaseUrl;
const frontendSupabasePublishableKey =
  runtimeConfig.frontendSupabasePublishableKey;
const clusterSupabaseUrl = runtimeConfig.clusterSupabaseUrl;
const clusterSupabasePublishableKey =
  runtimeConfig.clusterSupabasePublishableKey;

const createConfiguredClient = (name, url, publishableKey) => {
  const missing = [!url && "URL", !publishableKey && "publishable key"].filter(
    Boolean,
  );
  if (missing.length > 0) {
    const message = `${name} Supabase configuration is missing: ${missing.join(", ")}`;
    console.error(`[Frontend] ${message}`);
    throw new Error(message);
  }

  try {
    const client = createClient(url, publishableKey);
    if (!client) {
      throw new Error("Supabase SDK returned no client");
    }
    console.info(`[Frontend] ${name} Supabase client created successfully.`);
    return client;
  } catch (error) {
    console.error(
      `[Frontend] Failed to create ${name} Supabase client:`,
      error,
    );
    throw error;
  }
};

export const supabase = createConfiguredClient(
  "frontend",
  frontendSupabaseUrl,
  frontendSupabasePublishableKey,
);

export const clusterSupabase = createConfiguredClient(
  "cluster",
  clusterSupabaseUrl,
  clusterSupabasePublishableKey,
);

export const workspaceBackendUrl = "/api/backend";
export const workspaceAgentUrl = "/api/agent";
export const gitlabApiPrefix = "/api/gitlab";

let cachedSession = null;
let grafanaLoginReadyUntil = 0;
let grafanaLoginPromise = null;
const GRAFANA_LOGIN_READY_MS = 5 * 60 * 1000;
const GRAFANA_LOGIN_SKEW_MS = 30 * 1000;

const resetGrafanaLoginState = () => {
  grafanaLoginReadyUntil = 0;
  grafanaLoginPromise = null;
};

supabase.auth.onAuthStateChange((event, session) => {
  if (event === "SIGNED_OUT" || event === "USER_DELETED") {
    cachedSession = null;
    resetGrafanaLoginState();
  } else if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
    cachedSession = session;
    resetGrafanaLoginState();
  }
});

export const getAuthHeaders = async () => {
  if (!cachedSession) {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();

    if (error) {
      console.error("Error getting session:", error);
      return null;
    }
    if (!session) {
      console.warn("No active supabase session found.");
      return null;
    }
    cachedSession = session;
  }

  return {
    Authorization: `Bearer ${cachedSession.access_token}`,
    "Content-Type": "application/json",
  };
};

export const hasGrafanaLogin = () =>
  grafanaLoginReadyUntil - Date.now() > GRAFANA_LOGIN_SKEW_MS;

export const ensureGrafanaLogin = async () => {
  if (hasGrafanaLogin()) return true;
  if (grafanaLoginPromise) return grafanaLoginPromise;

  grafanaLoginPromise = (async () => {
    const headers = await getAuthHeaders();
    if (!headers) {
      throw new Error("User not authenticated or session expired.");
    }

    const response = await fetch("/protected/grafana/login", {
      headers,
      credentials: "same-origin",
    });
    if (!response.ok) {
      throw new Error(`Grafana login failed: ${response.status}`);
    }

    grafanaLoginReadyUntil = Date.now() + GRAFANA_LOGIN_READY_MS;
    return true;
  })().finally(() => {
    grafanaLoginPromise = null;
  });

  return grafanaLoginPromise;
};

export const logoutGrafana = async () => {
  resetGrafanaLoginState();

  try {
    await fetch("/grafana/logout", {
      credentials: "same-origin",
      redirect: "manual",
      cache: "no-store",
    });
  } catch (err) {
    console.warn("[SupabaseClient] Grafana logout failed:", err);
  }
};

export const supabaseAuthenticatedApi = {
  async request(method, prefix, url, body = null, timeout = 5000) {
    const headers = await getAuthHeaders();
    if (!headers) {
      throw new Error("User not authenticated or session expired.");
    }

    const controller = new AbortController();
    const signal = controller.signal;
    const options = {
      method,
      headers,
      body: body ? JSON.stringify(body) : null,
      signal,
    };

    const timeoutId = setTimeout(() => controller.abort(), timeout);

    // console.info(
    //   `Authenticated API request: ${method} ${prefix}${url}`,
    //   body ? `with body: ${JSON.stringify(body)}` : "",
    // );

    try {
      const response = await fetch(`${prefix}${url}`, options);
      clearTimeout(timeoutId);

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { message: response.statusText };
        }
        const error = new Error(
          errorData.detail ||
            errorData.message ||
            `HTTP error ${response.status}`,
        );
        error.status = response.status;
        error.data = errorData;
        throw error;
      }

      if (
        response.status === 204 ||
        response.headers.get("Content-Length") === "0"
      ) {
        return null;
      }
      return response;
    } catch (error) {
      if (error.name === "AbortError") {
        throw new Error("Request timed out. Please try again later.");
      }
      console.error("Error in authenticated API request:", error);
      throw error;
    }
  },

  get: function (url, prefix = frontendSupabaseUrl, timeout = 5000) {
    return this.request("GET", prefix, url, null, timeout);
  },
  post: function (url, body, prefix = frontendSupabaseUrl, timeout = 5000) {
    return this.request("POST", prefix, url, body, timeout);
  },
  put: function (url, body, prefix = frontendSupabaseUrl, timeout = 5000) {
    return this.request("PUT", prefix, url, body, timeout);
  },
  delete: function (url, prefix = frontendSupabaseUrl, timeout = 5000) {
    return this.request("DELETE", prefix, url, null, timeout);
  },
};
